import type { Rest } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import { BioForestAbpService } from 'orchestrator/proxy';
import type {
  FomentContractDto,
  GetFomentContractsInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/integrations/bio-forest-abp/models';
import { map, of } from 'rxjs';

type FomentContractListInput = Omit<GetFomentContractsInputDto, 'ids'> & {
  ids?: Array<string | number> | string | null;
  fomentContractId?: Array<string | number> | string;
};

@Injectable({ providedIn: 'root' })
export class FomentContractLookupService {
  private readonly bioForestAbpService = inject(BioForestAbpService);

  get = (id: string | number, config?: Partial<Rest.Config>) =>
    this.getListByIds({ ids: [id] }, config).pipe(map(items => items[0]));

  getList = (input: FomentContractListInput, config?: Partial<Rest.Config>) =>
    this.bioForestAbpService.getFomentContracts(
      {
        vendorId: input.vendorId,
        number: input.number,
        ids: input.fomentContractId !== undefined
          ? this.parseIds(input.fomentContractId)
          : this.normalizeIds(input.ids),
        skipCount: input.skipCount,
        maxResultCount: input.maxResultCount,
      },
      undefined,
      config,
    );

  getListByIds = (
    input: { ids: Array<string | number> | string },
    config?: Partial<Rest.Config>,
  ) => {
    const ids = this.parseIds(input.ids);

    if (!ids.length) {
      return of([] as FomentContractDto[]);
    }

    return this.getList(
      {
        ids,
        skipCount: 0,
        maxResultCount: Math.max(1, ids.length),
      },
      config,
    ).pipe(map(response => response.items ?? []));
  };

  private normalizeIds(ids: Array<string | number> | string | null | undefined): number[] | null | undefined {
    if (typeof ids === 'string' || Array.isArray(ids)) {
      return this.parseIds(ids);
    }

    return ids === null ? null : undefined;
  }

  private parseIds(ids: Array<string | number> | string): number[] {
    if (Array.isArray(ids)) {
      return ids.map(Number);
    }

    return ids
      .split(',')
      .map(id => id.trim())
      .filter(Boolean)
      .map(Number);
  }
}
